/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { gql, useMutation, useLazyQuery, useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  myOrders,
  placeOrder,
  evaluateCart,
  GET_DELIVERY_FEE,
  myQorSavedCards,
  abortOrder,
  myCheckoutCredit,
} from "../../apollo/server";
import {
  CartItemCard,
  DeliveryCard,
  PaymentCard,
  PaymentProcessing,
} from "../../components/Checkout";
import OrderOptions from "../../components/Checkout/OrderOptions";
import OtherCarts from "../../components/Checkout/OtherCarts";
import { CateringOrderBanner } from "../../components/Catering/Catering";
import { SLOT_STEP_MINUTES, generateTimeSlots, isOpenNow } from "../../components/Checkout/orderTimeSlots";
import { restaurantDayOf } from "../../utils/restaurantClock";
import QorPaymentSheet from "../../components/Checkout/QorPaymentSheet";
import { CARD_PAYMENT_OPTION, STRIPE_PAYMENT_OPTION } from "../../components/Checkout/PaymentCard";
import CheckoutHeader, { CHECKOUT_HEADER_HEIGHT } from "../../components/Checkout/CheckoutHeader";
import CheckoutAddressCard from "../../components/Checkout/CheckoutAddressCard";
import DropoffAndNotes from "../../components/Checkout/DropoffAndNotes";
import ContactDetails from "../../components/Checkout/ContactDetails";
import WalletCard from "../../components/Checkout/WalletCard";
import PlaceOrderButton from "../../components/Checkout/PlaceOrderButton";
import useSummaryData from "../../components/Checkout/useSummaryData";
import { StepCard, money, textButtonSx } from "../../components/Checkout/checkoutUi";
import CloseIcon from "@mui/icons-material/Close";

import FlashMessage from "../../components/FlashMessage";
import { RestaurantClose } from "../../components/Modals";
import ConfigurationContext from "../../context/Configuration";
import { useLocationContext } from "../../context/Location";
import { useOrderMode, ORDER_MODES } from "../../context/OrderMode";
import { useTimeSelect } from "../../context/Time";
import UserContext from "../../context/User";
import { useRestaurant } from "../../hooks";
import { paypalCurrencies, stripeCurrencies } from "../../utils/currencies";
import { isAuthError } from "../../utils/session";
import {
  cateringEarliest,
  cateringItemIds,
  cateringSettingsOf,
  describeLead,
  isCateringCart as cartHasCatering,
  readCateringPlan,
} from "../../utils/catering";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, TYPE } from "../../utils/designTokens";

import Analytics from "../../utils/analytics";
import ConfigurableValues from "../../config/constants";
import NearMeIcon from "@mui/icons-material/NearMe";

import { useTranslation } from "react-i18next";

const PLACEORDER = gql`
  ${placeOrder}
`;
const ORDERS = gql`
  ${myOrders}
`;
const EVALUATE_CART = gql`
  ${evaluateCart}
`;
const DELIVERY_FEE_QUERY = gql`
  ${GET_DELIVERY_FEE}
`;

const CHECKOUT_CREDIT = gql`${myCheckoutCredit}`;

const QOR_CARDS = gql`
  ${myQorSavedCards}
`;
const ABORT_ORDER = gql`
  ${abortOrder}
`;

// Card is the default (and, off pickup, the only) way to pay at checkout.
const PAYMENT = CARD_PAYMENT_OPTION;

// The tip the customer picked, remembered for this tab only. It lived purely in
// component state, so every mount fell back to the default pill: a refresh, or
// a step back to the menu and forward again, replaced a 10% or custom tip — and
// brought back a tip the customer had deliberately removed. Per tab rather than
// stored for good, so tomorrow's order starts from the default again.
const TIP_STORAGE_KEY = "checkoutTip";
function readSavedTip(restaurantId) {
  if (!restaurantId) return null;
  try {
    const raw = sessionStorage.getItem(TIP_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    // Only for the cart it was chosen on — a custom $20 on one restaurant must
    // not follow the customer to the next.
    return saved && String(saved.restaurantId) === String(restaurantId) ? saved : null;
  } catch (e) {
    return null; // storage blocked (private window) — the tip just won't persist
  }
}

function Checkout() {
  const { t } = useTranslation();
  const { SERVER_URL } = ConfigurableValues();
  const navigate = useNavigate();
  const [isClose, setIsClose] = useState(false);
  const [mainError, setMainError] = useState({});
  const [cartCleared, setCartCleared] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const configuration = useContext(ConfigurationContext);
  const [addressModal, setAddressModal] = useState(false);
  // True when Place Order was pressed with an unsaved delivery address — opens
  // the address modal straight into the structured detail form.
  const [forceAddressDetail, setForceAddressDetail] = useState(false);
  // The order options are on the page now. Two flows used to open a dialog to
  // get here — the closed-restaurant popup and the "item unavailable at that
  // time" confirm — so they scroll to the block and flash its border instead.
  const orderOptionsRef = useRef(null);
  const [highlightOrderOptions, setHighlightOrderOptions] = useState(false);
  const focusOrderOptions = useCallback(() => {
    orderOptionsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightOrderOptions(true);
    setTimeout(() => setHighlightOrderOptions(false), 2000);
  }, []);
  const fetchRef = useRef(false);

  const [loadingLocation, setLoadingLocation] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const {
    profile,
    clearCart,
    restaurant: cartRestaurant,
    cart,
    cartCount,
    addQuantity,
    removeQuantity,
    deleteItem,
    isLoading: userLoading,
    fetchOrders,
    getCartMeta,
  } = useContext(UserContext);
  // A food-truck stop the cart was built at (backend migration 145): its menu,
  // prices, counts, deals and pickup point all follow it to the order.
  const cartStopId = (cartRestaurant && getCartMeta()?.stopId) || null;
  // Deals, rewards and tip options start loading now, beside the menu, rather
  // than once the menu has arrived.
  const summaryData = useSummaryData(cartRestaurant, cartStopId);

  const { location, setLocation } = useLocationContext();
  const { orderMode, setOrderMode } = useOrderMode();
  // const { getCurrentLocation } = useLocation();
  const theme = useTheme();
  // `undefined` means nobody has chosen yet (CartItemCard then applies the
  // default pill); `null` means the customer removed the tip on purpose.
  const restoredTip = React.useMemo(() => readSavedTip(cartRestaurant), [cartRestaurant]);
  const [selectedTip, setSelectedTip] = useState(restoredTip ? restoredTip.tip : undefined);
  const [customTipAmount, setCustomTipAmount] = useState(restoredTip ? restoredTip.custom || '' : '');
  const tipDecided = Boolean(restoredTip);
  const tipTouchedRef = useRef(Boolean(restoredTip));

  useEffect(() => {
    // Don't write before anything is decided: saving `null` on a first mount
    // would look like "removed" and stop the default pill applying later.
    if (selectedTip === undefined && !tipTouchedRef.current) return;
    if (!cartRestaurant) return;
    tipTouchedRef.current = true;
    try {
      sessionStorage.setItem(
        TIP_STORAGE_KEY,
        JSON.stringify({
          restaurantId: String(cartRestaurant),
          tip: selectedTip ?? null,
          custom: customTipAmount || '',
        })
      );
    } catch (e) { /* storage blocked — the tip just won't persist */ }
  }, [selectedTip, customTipAmount, cartRestaurant]);
  const [selectedDriverTip, setSelectedDriverTip] = useState();
  const [customDriverTipAmount, setCustomDriverTipAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT);
  const [taxValue, setTaxValue] = useState();
  // QorCommerce payment sheet + captured token.
  const [qorModalOpen, setQorModalOpen] = useState(false);
  const [qorToken, setQorToken] = useState(null);
  // Why the sheet is open: "manage" (opened from the payment block — picking a
  // card only changes the default) or "pay" (Place Order was pressed with no
  // card on file — a card entered here charges straight away).
  const [qorSheetPurpose, setQorSheetPurpose] = useState("manage");
  // The saved card being paid with. null until the cards load; a token once one
  // is chosen (the first saved card becomes the default automatically).
  const [qorSavedCardToken, setQorSavedCardToken] = useState(null);
  // Adding a new card always saves it to the customer's account (DoorDash-style
  // implicit save — the form's button reads "Save & Continue"). No checkbox.
  const [qorSaveCard] = useState(true);
  // Stripe's live instances from the card form in the Stripe tile — null while
  // Stripe isn't selected or Stripe.js is still loading.
  const stripeFormRef = useRef(null);
  // True only while stripe.confirmPayment runs — see PaymentProcessing's warnOnLeave.
  const [stripeConfirming, setStripeConfirming] = useState(false);

  const [coupon, setCoupon] = useState({});
  const [promoResult, setPromoResult] = useState(null);
  const [selectedPromoId, setSelectedPromoId] = useState(null);
  const [loyaltyVoucherCode, setLoyaltyVoucherCode] = useState(null);
  const [loyaltyCouponCode, setLoyaltyCouponCode] = useState(null);
  const [loyaltyCouponDiscount, setLoyaltyCouponDiscount] = useState(0);
  const [walletAmountToUse, setWalletAmountToUse] = useState(0);
  const [walletInputValue, setWalletInputValue] = useState('');

  // Credit usable here: wallet balance plus store credit at this restaurant.
  // Falls back to the wallet balance alone until it loads.
  const { data: checkoutCreditData } = useQuery(CHECKOUT_CREDIT, {
    variables: { restaurantId: cartRestaurant },
    skip: !profile?._id || !cartRestaurant,
    fetchPolicy: "network-only",
  });
  const storeCreditHere = parseFloat(checkoutCreditData?.myCheckoutCredit?.store || 0);
  const walletAvailable = checkoutCreditData?.myCheckoutCredit
    ? parseFloat(checkoutCreditData.myCheckoutCredit.total || 0)
    : parseFloat(profile?.walletBalance || 0);

  // Saved cards live here (not inside the sheet) so the payment block, the
  // sheet and placeOrder all read one selection and one list.
  const {
    data: qorCardsData,
    loading: qorCardsLoading,
    refetch: refetchQorCards,
  } = useQuery(QOR_CARDS, {
    fetchPolicy: "network-only",
    skip: !profile?._id,
  });

  // The backend dedupes vault duplicates, but stale cache or an older backend
  // must never show "....1881" three times.
  const qorCards = React.useMemo(() => {
    const seen = new Set();
    return (qorCardsData?.myQorSavedCards ?? []).filter((c) => {
      const key = `${c.brand || ""}|${c.last4 || ""}|${c.expiry || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [qorCardsData]);

  // First saved card becomes the default; a deleted default falls back to
  // whatever is left (nothing, if the account is now empty).
  useEffect(() => {
    if (!qorCards.length) {
      if (qorSavedCardToken) setQorSavedCardToken(null);
      return;
    }
    if (!qorCards.some((c) => c.token === qorSavedCardToken)) {
      setQorSavedCardToken(qorCards[0].token);
    }
  }, [qorCards]);

  // An admin can turn a card gateway off (Configuration → Card payment
  // gateways). If the selected one is off, move to the card option that is on,
  // so Place Order never asks the server for a gateway it will refuse.
  const qorPaymentsOn = configuration?.qorPaymentsEnabled !== false;
  const stripePaymentsOn = configuration?.stripePaymentsEnabled !== false;
  useEffect(() => {
    if (paymentMethod?.payment === "QOR" && !qorPaymentsOn && stripePaymentsOn) {
      setPaymentMethod(STRIPE_PAYMENT_OPTION);
    } else if (paymentMethod?.payment === "STRIPE" && !stripePaymentsOn && qorPaymentsOn) {
      setPaymentMethod(CARD_PAYMENT_OPTION);
    }
  }, [qorPaymentsOn, stripePaymentsOn, paymentMethod?.payment]);

  const [runEvaluateCart] = useLazyQuery(EVALUATE_CART, {
    fetchPolicy: 'network-only',
    onError: () => { /* non-critical — fall back to legacy coupon */ },
  });
  // A chosen deal or code waiting for its price. Only the latest request may
  // answer — an older one landing late must not replace a newer choice — and
  // re-checking the same choice (a menu refresh, say) doesn't count as waiting.
  const [promoPending, setPromoPending] = useState(false);
  const evalSeqRef = useRef(0);
  const evalKeyRef = useRef(null);

  const { loading, data, error } = useRestaurant(cartRestaurant, undefined, cartStopId);
  const restaurantData = data?.restaurant ?? null;

  // Catering (backend migration 131): a cart of catering items checks out on
  // the restaurant's catering terms — always scheduled at least the notice
  // ahead, with its own delivery options, fees and minimum. The cart's kind is
  // told from its items; nothing extra is stored on the cart.
  const cateringIds = React.useMemo(() => cateringItemIds(restaurantData), [restaurantData]);
  const isCateringCart = cartHasCatering(cart, cateringIds);
  const cateringSettings = React.useMemo(
    () => (isCateringCart ? cateringSettingsOf(restaurantData) : null),
    [isCateringCart, restaurantData]
  );

  const { mode: timeMode, scheduledFor, getOrderDate, selectNow, selectScheduled } = useTimeSelect();
  // Initialise the checkout's order time from the header selection. If the
  // user changes it here, local state overrides the header for this session.
  const [selectedDate, handleDateChange] = useState(() => getOrderDate());
  const [isPickUp, setIsPickUp] = useState(() => localStorage.getItem('orderMode') === ORDER_MODES.PICKUP);
  const [isAsap, setIsAsap] = useState(timeMode === 'now');

  // Selecting on this page also updates the header's Delivery/Pickup toggle and
  // Now/Schedule chip. Without this the two disagree, and the sync effects below
  // can overwrite what the customer just chose.
  const choosePickUp = useCallback((pickup) => {
    setIsPickUp(pickup);
    setOrderMode(pickup ? ORDER_MODES.PICKUP : ORDER_MODES.DELIVERY);
  }, [setOrderMode]);

  const chooseOrderDate = useCallback((date) => {
    handleDateChange(date);
    // A date at (or before) now is the ASAP choice; anything later is scheduled.
    if (date && date.getTime() > Date.now() + 60000) selectScheduled(date);
    else selectNow();
  }, [selectNow, selectScheduled]);
  // A catering time stays on this page: it is a day or more ahead, and must
  // not become the header's time for the customer's next regular order.
  const chooseCateringDate = useCallback((date) => handleDateChange(date), []);

  // Sync if the header selection changes while user is on the checkout page.
  React.useEffect(() => {
    if (timeMode === 'now') {
      setIsAsap(true);
      handleDateChange(new Date());
    } else if (scheduledFor) {
      setIsAsap(false);
      handleDateChange(scheduledFor);
    }
  }, [timeMode, scheduledFor]);

  // A catering checkout starts on the event planned on the Catering page
  // ("Schedule for Oct 24"): the first open slot that day at or after it.
  // Declared after the sync above so it wins on arrival; OrderOptions still
  // moves it on if it falls inside the notice.
  const cateringPlanUsedRef = useRef(false);
  React.useEffect(() => {
    if (!isCateringCart || cateringPlanUsedRef.current || !restaurantData) return;
    cateringPlanUsedRef.current = true;
    const plan = readCateringPlan(cartRestaurant);
    if (!plan) return;
    // The planned time's day on the restaurant's clock.
    const day = restaurantDayOf(plan.at, restaurantData.timezone);
    const step = isPickUp ? restaurantData.preparationTime ?? 15 : 30;
    const floor = cateringEarliest(cateringSettings?.leadTimeMinutes || 0);
    const slot = generateTimeSlots(day, step, restaurantData.openingTimes, SLOT_STEP_MINUTES, floor, restaurantData.timezone).find(
      (s) => s.getTime() >= plan.at.getTime()
    );
    if (!slot) return;
    setIsAsap(false);
    handleDateChange(slot);
  }, [isCateringCart, restaurantData]);

  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [deliveryZoneName, setDeliveryZoneName] = useState('');
  const [deliveryAvailable, setDeliveryAvailable] = useState(true);
  // Tracks whether the delivery-fee lookup has finished (success OR error), so
  // promo evaluation can tell "fee is genuinely $0 / free" apart from "fee not
  // loaded yet" — otherwise free-delivery orders never evaluate promos.
  const [deliveryFeeLoaded, setDeliveryFeeLoaded] = useState(false);
  const [noZonesConfigured, setNoZonesConfigured] = useState(false);
  const [isSelfDelivery, setIsSelfDelivery] = useState(false);
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  // How the driver hands this order over; starts from the saved address.
  const [dropoffOption, setDropoffOption] = useState(null);
  // Tracks whether the customer has typed their own instructions, so seeding
  // from the saved address doesn't overwrite what they wrote. Reset whenever
  // the delivery address changes — see the seeding effect below.
  // Set the moment a paid order comes back, and never unset: from then on this
  // screen is on its way to order detail and must never fall through to the
  // "Your cart is empty" state, no matter what the cart does behind it.
  const orderPlacedRef = useRef(false);
  // Drives the placing overlay. Deliberately NOT just the mutation's `loading`:
  // the work is not over when placeOrder resolves — onCompleted still refetches
  // the orders list before it can navigate, and closing the overlay there put
  // the customer back on a live checkout screen, cart and Place Order button
  // and all, seconds after they had paid. `success` keeps it up and turns it
  // into a confirmation until order detail takes over.
  const [placingPhase, setPlacingPhase] = useState(null); // null | 'processing' | 'success'
  const [placedOrderId, setPlacedOrderId] = useState(null);
  const instructionsEditedRef = useRef(false);
  // Which address the box was last seeded from, so a change of address is
  // distinguishable from the same address's instructions being updated.
  const seededAddressIdRef = useRef(null);
  // Server-authoritative Black Card delivery discount for THIS member at THIS
  // address (0 when not eligible, e.g. beyond 5 miles). Display/charge must use
  // this — never assume the discount applies just because hasBlackCard is true.
  const [blackCardDiscount, setBlackCardDiscount] = useState(0);

  const [fetchDeliveryFee] = useLazyQuery(DELIVERY_FEE_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const result = data?.getDeliveryFee;
      if (result?.available) {
        setDeliveryCharges(result.fee);
        setDeliveryZoneName(result.zoneName || '');
        setDeliveryAvailable(true);
        setNoZonesConfigured(false);
        setIsSelfDelivery(result?.isSelfDelivery === true);
        setBlackCardDiscount(parseFloat(result?.blackCardDiscount || 0));
      } else {
        setDeliveryCharges(0);
        setDeliveryZoneName('');
        setDeliveryAvailable(false);
        setNoZonesConfigured(result?.noZonesConfigured === true);
        setBlackCardDiscount(0);
      }
      setDeliveryFeeLoaded(true);
    },
    onError: () => {
      setDeliveryCharges(0);
      setDeliveryAvailable(false);
      setNoZonesConfigured(false);
      setBlackCardDiscount(0);
      setDeliveryFeeLoaded(true);
    },
  });

  // Sync isPickUp when orderMode loads from localStorage
  useEffect(() => {
    setIsPickUp(orderMode === ORDER_MODES.PICKUP);
  }, [orderMode]);

  // Evaluate promotions server-side whenever cart changes or customer selects a promo
  useEffect(() => {
    const clearEvaluation = () => {
      evalSeqRef.current += 1;
      evalKeyRef.current = null;
      setPromoPending(false);
      setPromoResult(null);
    };
    if (!cartRestaurant || !cart || cart.length === 0) {
      clearEvaluation();
      return;
    }
    // Only evaluate if customer has explicitly chosen a promo or coupon
    const couponCode = loyaltyVoucherCode || coupon?.title || null;
    // selectedPromoId is the _id of a deal picked by the customer (AUTOMATIC or COUPON_CODE)
    if (!selectedPromoId && !couponCode) {
      clearEvaluation();
      return;
    }
    const subtotal = cart.reduce((sum, item) => sum + parseFloat(item.price || 0) * item.quantity, 0);
    const evalKey = [selectedPromoId, couponCode, subtotal.toFixed(2), isPickUp, deliveryCharges, isCateringCart].join("|");
    const seq = ++evalSeqRef.current;
    if (evalKey !== evalKeyRef.current) setPromoPending(true);
    // Wait for the delivery-fee lookup to finish (not for a nonzero fee) so
    // free-delivery orders still evaluate promos and show applied/rejected.
    if (!isPickUp && !deliveryFeeLoaded) return;

    // Build foodId → categoryName map from advanced menus so REQUIRED_CATEGORY conditions work
    const foodCategoryMap = {};
    (restaurantData?.advancedMenus || []).forEach(menu => {
      (menu.items || []).forEach(item => {
        if (item._id) foodCategoryMap[item._id] = item.categoryName || menu.name || '';
      });
    });
    // Also map from classic categories
    (restaurantData?.categories || []).forEach(cat => {
      (cat.foods || []).forEach(food => {
        if (food._id) foodCategoryMap[food._id] = cat.title || '';
      });
    });

    runEvaluateCart({
      variables: {
        input: {
          restaurantId: cartRestaurant,
          userId: profile?._id ? String(profile._id) : null,
          channel: isPickUp ? 'PICKUP' : 'DELIVERY',
          cartItems: cart.map(item => ({
            foodId: item._id,
            categoryId: item.categoryId || null,
            categoryName: foodCategoryMap[item._id] || null,
            unitPrice: parseFloat(item.price || 0),
            quantity: item.quantity,
            title: item.title || '',
            isActive: true,
          })),
          subtotal,
          deliveryFee: isPickUp ? 0 : deliveryCharges,
          promoCode: couponCode,
          selectedPromoId: selectedPromoId || null,
          customerLatitude: !isPickUp && location?.latitude ? parseFloat(location.latitude) : null,
          customerLongitude: !isPickUp && location?.longitude ? parseFloat(location.longitude) : null,
          // Catering deals apply to catering carts only.
          isCatering: isCateringCart,
          // Deals limited to other truck stops don't apply.
          stopId: cartStopId,
        },
      },
    }).then((res) => {
      if (seq !== evalSeqRef.current) return;
      if (res?.data?.evaluateCart) setPromoResult(res.data.evaluateCart);
      evalKeyRef.current = evalKey;
      setPromoPending(false);
    }).catch(() => {
      if (seq === evalSeqRef.current) setPromoPending(false);
    });
  }, [cart, coupon, loyaltyVoucherCode, selectedPromoId, deliveryCharges, deliveryFeeLoaded, isPickUp, cartRestaurant, restaurantData, isCateringCart]);

  let restCoordinates = {};
  const extraSmall = useMediaQuery(theme.breakpoints.down("sm"));
  // Two columns (steps | sticky summary) from md up; below that the summary
  // follows the steps and Place order rides in a bar along the bottom.
  const twoColumn = useMediaQuery(theme.breakpoints.up("md"));
  // The summary sticks 24px under the header while it fits on screen. Taller
  // than the window, it scrolls up with the page until its bottom — the total
  // and Place order — is in view, and sticks there, so nothing in it is ever
  // out of reach behind an inner scrollbar.
  const SUMMARY_TOP = CHECKOUT_HEADER_HEIGHT + 24;
  const [summaryTop, setSummaryTop] = useState(SUMMARY_TOP);
  const summaryWatch = useRef(null);
  const summaryRef = useCallback((el) => {
    if (summaryWatch.current) summaryWatch.current();
    summaryWatch.current = null;
    if (!el) return;
    const update = () =>
      setSummaryTop(Math.min(SUMMARY_TOP, Math.round(window.innerHeight - el.getBoundingClientRect().height - 24)));
    update();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", update);
    summaryWatch.current = () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);
  const [mutateOrder, { loading: loadingOrderMutation }] = useMutation(
    PLACEORDER,
    {
      onCompleted,
      onError,
      update,
    }
  );
  const [abortOrderMutation] = useMutation(ABORT_ORDER);

  // No redirect — empty cart shows empty state on checkout page

  useEffect(() => {
    if (!location) {
      let localStorageLocation = localStorage.getItem("location");
      localStorageLocation = JSON.parse(localStorageLocation);
      if (localStorageLocation) {
        setLocation(localStorageLocation);
      }
    }
  }, []);

  // An order scheduled more than an hour ahead is quoted at its delivery time:
  // a restaurant can hand part of its day to its own drivers, and the fee
  // follows whoever delivers then. ASAP orders are quoted now, as the server
  // charges them.
  const scheduledFeeAt =
    !isAsap && selectedDate && new Date(selectedDate).getTime() > Date.now() + 60 * 60 * 1000
      ? new Date(selectedDate).toISOString()
      : null;
  // A catering order is always quoted at its wanted time, on catering terms.
  const cateringFeeAt =
    isCateringCart && selectedDate ? new Date(selectedDate).toISOString() : null;

  // Fetch delivery fee from server based on customer address + restaurant delivery zones
  useEffect(() => {
    if (data?.restaurant && location && !isPickUp) {
      // New lookup in flight — promo evaluation should wait for it to finish.
      setDeliveryFeeLoaded(false);
      fetchDeliveryFee({
        variables: {
          restaurantId: data.restaurant._id,
          latitude: parseFloat(location.latitude),
          longitude: parseFloat(location.longitude),
          at: isCateringCart ? cateringFeeAt : scheduledFeeAt,
          catering: isCateringCart,
          stopId: cartStopId,
        },
      });
    } else if (isPickUp) {
      setDeliveryCharges(0);
      setDeliveryAvailable(true);
      setDeliveryFeeLoaded(true);
    }
    // Keyed on the restaurant and the address, not the menu object: the menu
    // refreshes in the background a couple of seconds after checkout opens,
    // and re-pricing delivery then made a deal picked meanwhile wait for it.
  }, [data?.restaurant?._id, location?.latitude, location?.longitude, isPickUp, scheduledFeeAt, isCateringCart, cateringFeeAt]);

  // Shares the same implementation the order-options block uses, so the page
  // can't decide the restaurant is open while the ASAP radio says it is closed.
  // A food-truck stop takes orders for its window today; the server has
  // already moved an early order to when the truck gets there.
  const isOpen = () => (data?.restaurant?.orderingStop
    ? data.restaurant.orderingStop.canOrder === true
    : isOpenNow(data?.restaurant?.openingTimes || [], data?.restaurant?.timezone));
  // Opening straight into the structured form for the address already
  // selected — what the Deliver to rows do. The list view is for picking a
  // different address; these rows mean "edit this one".
  const openAddressDetail = useCallback(() => {
    setForceAddressDetail(true);
    setAddressModal(true);
  }, []);

  const toggleAdressModal = useCallback(() => {
    setAddressModal((prev) => {
      // Clear the forced-detail flag whenever the modal closes so a later
      // manual "Change address" opens the normal address list.
      if (prev) setForceAddressDetail(false);
      return !prev;
    });
  }, []);

  useEffect(() => {
    // A catering order is always placed for later, so "closed now" is moot.
    if (isCateringCart) return;
    if (cart && cartCount > 0) {
      if (
        data &&
        data.restaurant &&
        (!data.restaurant.isAvailable || !isOpen())
      ) {
        setIsClose((prev) => {
          if (!prev) return true;
          else return prev;
        });
      }
    }
  }, [data]);

  // Recover from a dead cart: if the cart references a restaurant that the
  // backend can no longer resolve ("Restaurant not found" / removed / null),
  // the restaurant query settles with an error or no restaurant. Without this
  // the page hangs on the spinner (or shows a dead-end error) with no way out.
  // Clear the stale cart + restaurant id and send the user home.
  useEffect(() => {
    if (!cartRestaurant || loading) return;
    if (error || (data && !data.restaurant)) {
      clearCart();
      navigate("/", { replace: true });
    }
  }, [loading, error, data, cartRestaurant]);

  const setDeliveryAddress = (item) => {
    setSelectedAddress(item);
    setLocation({
      _id: item?._id,
      label: item?.label,
      latitude: Number(item?.location.coordinates[1]),
      longitude: Number(item?.location.coordinates[0]),
      deliveryAddress: item?.deliveryAddress,
      details: item?.details,
      apartment: item?.apartment,
      buildingName: item?.buildingName,
      entryCode: item?.entryCode,
      dropoffOption: item?.dropoffOption,
      instructions: item?.instructions,
    });
  };

  // Seed the Delivery Instructions box from the saved address, per address.
  //
  // Two ways this used to show the wrong note: it only wrote when the new
  // address HAD instructions, so switching to one without them left the
  // previous address's text sitting in the box; and `instructionsEdited` was
  // sticky for the whole session, so once the customer typed anything, no
  // later address ever re-seeded. Both meant address A's note riding along to
  // address B.
  //
  // Now the seed is keyed to the address: changing address clears the typed
  // flag and adopts that address's instructions (empty when it has none),
  // while staying on one address still protects whatever was typed for it.
  useEffect(() => {
    const addressId = location?._id ?? null;
    const sameAddress = seededAddressIdRef.current === addressId;
    if (sameAddress) {
      if (instructionsEditedRef.current) return;
    } else {
      seededAddressIdRef.current = addressId;
      instructionsEditedRef.current = false;
    }
    setDeliveryInstructions(location?.instructions || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?._id, location?.instructions]);

  useEffect(() => {
    setDropoffOption(location?.dropoffOption || null);
  }, [location?._id, location?.dropoffOption]);

  const onChangeDeliveryInstructions = useCallback((value) => {
    instructionsEditedRef.current = true;
    setDeliveryInstructions(value);
  }, []);

  const toggleCloseModal = useCallback(() => {
    setIsClose((prev) => !prev);
  }, []);

  const showMessage = useCallback((messageObj) => {
    setMainError(messageObj);
  }, []);

  const toggleSnackbar = useCallback(() => {
    setMainError({});
  }, []);

  const currency = configuration?.currencySymbol || "$";
  const backTo = data?.restaurant?.slug ? `/restaurant/${data.restaurant.slug}` : "/";

  // The header and a centred message, for every state that isn't the form.
  const centered = (content) => (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg }}>
      <CheckoutHeader backTo={backTo} />
      <Box
        sx={{
          minHeight: `calc(100vh - ${CHECKOUT_HEADER_HEIGHT}px)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          px: 2,
          textAlign: "center",
        }}
      >
        {content}
      </Box>
    </Box>
  );
  const spinner = <CircularProgress size={48} sx={{ color: C.red }} aria-label="Loading checkout" />;

  // An order that has already been paid for is mid-navigation to order detail —
  // hold the spinner rather than flashing the empty-cart dead end at someone who
  // was just charged.
  if (!cartCount && orderPlacedRef.current) {
    return (
      <>
        {centered(spinner)}
        {/* This branch returns before the main tree, so the overlay has to be
            repeated here or it would vanish — a bare spinner is precisely the
            "did it work?" moment it exists to prevent. */}
        <PaymentProcessing
          phase={placingPhase || (loadingOrderMutation ? "processing" : null)}
          amount={calculateTotal()}
          currencySymbol={currency}
          paymentMethod={paymentMethod?.payment}
          orderId={placedOrderId}
        />
      </>
    );
  }

  if (!cartCount) {
    return centered(
      <>
        <Box component="h1" sx={{ ...TYPE.sectionTitle, m: 0, color: C.ink }}>Your cart is empty</Box>
        <Box sx={{ ...TYPE.body, color: C.muted }}>Add items from a restaurant to get started</Box>
        <Box component={RouterLink} to={backTo} sx={{ ...textButtonSx, fontSize: 15, mt: 1 }}>
          Back to menu
        </Box>
        {/* Parked carts belong here most of all: right after switching
            restaurants the active cart IS empty, and this screen used to be
            a dead end that made a parked cart look deleted. */}
        <Box sx={{ width: "100%", maxWidth: 560, mt: 2, textAlign: "left" }}>
          <OtherCarts />
        </Box>
      </>
    );
  }

  // A menu already in the cache is enough to show checkout: the background
  // refresh (2-3s for a full menu) lands underneath, as on the restaurant page.
  if ((loading && !data?.restaurant) || loadingData || userLoading || !cartRestaurant) {
    return centered(spinner);
  }

  if (error && !data?.restaurant) {
    return centered(<Box sx={{ ...TYPE.body, color: C.ink }}>Unable to fetch data</Box>);
  }

  if (!data?.restaurant) {
    return centered(<Box sx={{ ...TYPE.body, color: C.ink }}>Restaurant not found</Box>);
  }

  restCoordinates = {
    lat: parseFloat(data.restaurant.location.coordinates[1]),
    lng: parseFloat(data.restaurant.location.coordinates[0]),
  };

  function update(cache, { data: { placeOrder } }) {
    if (!placeOrder) return;
    // Seed the orders cache for EVERY payment method, not just COD. Order detail
    // renders from this list, so a method that lands there directly (COD, and QOR
    // which charges synchronously) showed an endless spinner until a manual
    // refresh refetched the list. Harmless for the redirect methods — their order
    // simply appears in the list already, and is refreshed like any other.
    try {
      const data = cache.readQuery({ query: ORDERS });
      if (data) {
        const rest = data.orders.filter((o) => o._id !== placeOrder._id);
        cache.writeQuery({
          query: ORDERS,
          data: { orders: [placeOrder, ...rest] },
        });
      }
    } catch (e) { /* cache miss — order detail refetches for itself */ }
  }

  function onError(error) {
    console.log("Check-out Error", error);
    // Take the overlay down so the toast below is visible and the customer can
    // pick another card. A decline is rolled back server side, so there is
    // nothing placed to go and look at.
    setPlacingPhase(null);
    // A rejected token surfaces here as the bare word "Unauthenticated", which
    // told the customer nothing. The Apollo error link is already sending them
    // to sign in; say so in the toast instead of showing the raw server text.
    const authRejected =
      isAuthError(error) ||
      (error?.graphQLErrors || []).some((err) => isAuthError(err));
    showMessage({
      type: "error",
      message: authRejected
        ? "Session expired. Please login again."
        : error.message,
    });
  }

  function calculateTipAmount(tipSelection, customAmount) {
    if (tipSelection === 'custom') {
      return parseFloat(customAmount || 0);
    }
    if (tipSelection) {
      // Percentage tips are based on the ORIGINAL item subtotal only:
      // no delivery fee, no tax, and before any promotion/coupon/loyalty discount
      // (pass delivery=0 and withDiscount=false).
      const base = +calculatePrice(0, false);
      return parseFloat(((base / 100) * parseFloat(tipSelection)).toFixed(2));
    }
    return 0;
  }

  // For delivery orders the customer leaves ONE tip; we split it server/driver
  // using the global serverTipShare from configuration (default 10% server).
  // For pickup, the entire tip belongs to the server (no driver involved).
  const serverTipShare = Math.max(
    0,
    Math.min(1, parseFloat(configuration?.serverTipShare ?? 0.10) || 0)
  );

  function calculateServerTip() {
    if (isPickUp) {
      // Pickup: single tip goes entirely to the server.
      return calculateTipAmount(selectedTip, customTipAmount);
    }
    // Delivery: split the single tip — server portion only.
    const totalTip = calculateTipAmount(selectedTip, customTipAmount);
    return parseFloat((totalTip * serverTipShare).toFixed(2));
  }

  function calculateDriverTip() {
    if (isPickUp) return 0;
    // Delivery: whatever the server didn't take of the single tip.
    const totalTip = calculateTipAmount(selectedTip, customTipAmount);
    return parseFloat((totalTip - calculateServerTip()).toFixed(2));
  }

  // Sum used by the order total. On delivery this is the customer-facing tip;
  // on pickup it's just the server tip.
  function calculateTip() {
    if (isPickUp) return calculateServerTip().toFixed(2);
    return calculateTipAmount(selectedTip, customTipAmount).toFixed(2);
  }

  function taxCalculation() {
    const tax = taxValue ?? 0;
    if (tax === 0) {
      return (0).toFixed(2);
    }
    // Tax is calculated on subtotal only (items, no delivery)
    const subtotal = +calculatePrice(0, true);
    const taxAmount = ((subtotal / 100) * tax).toFixed(2);
    return taxAmount;
  }
  async function onCompleted(data) {
    orderPlacedRef.current = true;
    setPlacedOrderId(data?.placeOrder?.orderId ?? null);
    // Only COD and QOR finish here — Stripe still has its charge ahead of it
    // (payStripeOrder) and the redirect methods a whole screen, so none of
    // them may be told the order is confirmed yet.
    // Flipped before anything that can take time or throw: the money has moved
    // by now, and the customer is owed that news immediately rather than after
    // the orders refetch.
    if (paymentMethod.payment === "COD" || paymentMethod.payment === "QOR") {
      setPlacingPhase("success");
    }
    const confirmedAt = Date.now();
    // ⚠️ By the time this runs the order EXISTS and the card is already charged.
    // Nothing optional may sit between here and the navigation: analytics is
    // fire-and-forget, and anything that can throw is contained. Previously this
    // awaited Analytics.track first, and a rejected track() (its config fetch can
    // resolve undefined) aborted the whole handler — leaving a paid customer
    // staring at the spinner on an order they'd already been emailed about.
    Analytics.track(Analytics.events.ORDER_PLACED, {
      userId: data.placeOrder.user._id,
      name: data.placeOrder.user.name,
      email: data.placeOrder.user.email,
      phoneNumber: data.placeOrder.user.phone,
      orderId: data.placeOrder.orderId,
      restaurantName: data.placeOrder.restaurant.name,
      restaurantAddress: data.placeOrder.restaurant.address,
      orderItems: data.placeOrder.items,
      orderPaymentMethod: data.placeOrder.paymentMethod,
      orderAmount: data.placeOrder.orderAmount,
      orderPaidAmount: data.placeOrder.paidAmount,
      tipping: data.placeOrder.tipping,
      orderStatus: data.placeOrder.orderStatus,
      orderDate: data.placeOrder.orderDate,
    }).catch(() => { /* analytics must never block a placed order */ });
    try {
      if (data.placeOrder.loyaltyEarnedMessage) {
        sessionStorage.setItem("loyaltyEarnedMessage", data.placeOrder.loyaltyEarnedMessage);
      }
    } catch (e) { /* private-mode storage — not worth losing the order screen */ }
    if (paymentMethod.payment === "COD" || paymentMethod.payment === "QOR") {
      // QorCommerce charges synchronously inside placeOrder — on success the
      // order is already PAID, so finish like COD (clear cart → order detail).
      setQorModalOpen(false);
      setQorToken(null);
      // Refetch the orders list BEFORE navigating — order detail renders by
      // finding the id in that list, so arriving before the order is in it means
      // an endless spinner. This mirrors what the NMI and Stripe screens already
      // do after a successful charge. Seeding the cache from the mutation's
      // update() is NOT equivalent in practice: the context reads orders through
      // a network-only useLazyQuery, and a hand-written cache entry did not
      // surface there (verified — the spinner persisted with that fix alone).
      try {
        if (typeof fetchOrders === "function") await fetchOrders();
      } catch (e) { /* order detail refetches once on its own if this fails */ }
      // The refetch usually outlasts this on its own, so it almost never waits.
      // It exists so a fast one cannot flash the confirmation past too quickly
      // to read — being told the payment worked is the whole point of it.
      const dwell = 1200 - (Date.now() - confirmedAt);
      if (dwell > 0) await new Promise((r) => setTimeout(r, dwell));
      navigate(`/order-detail/${data.placeOrder._id}`, { replace: true });
      // Emptying the cart LAST, after the route has changed. Doing it first
      // dropped `cart`/`cartRestaurant` while this screen was still mounted, so
      // the customer watched "Your cart is empty" for the whole length of the
      // orders refetch on an order they had just paid for. Nothing below this
      // line is allowed to block the navigation.
      try {
        await clearCart();
      } catch (e) { /* a stuck cart must not strand the customer on checkout */ }
    } else if (paymentMethod.payment === "PAYPAL") {
      navigate(`/paypal?id=${data.placeOrder._id}`, { replace: true });
    } else if (paymentMethod.payment === "STRIPE") {
      await payStripeOrder(data.placeOrder);
    } else if (paymentMethod.payment === "NMI") {
      navigate(`/nmi?id=${data.placeOrder._id}`, { replace: true });
    }
  }

  // The second half of a Stripe Place Order. The card was collected (and
  // validated by elements.submit) in the Stripe tile before the order existed;
  // now that placeOrder has priced it, create its PaymentIntent and charge that
  // card without leaving checkout. The overlay has been up since the press and
  // stays up through to order detail.
  async function payStripeOrder(order) {
    const isPaid = (intent) =>
      intent?.status === "succeeded" || intent?.status === "processing";

    // Nothing was charged: take the unpaid order back out (abortOrder deletes a
    // PAYMENT_PENDING order outright, as leaving the old /stripe screen did) and
    // hand the page back, so the customer can fix the card and press again.
    const giveBack = async (message) => {
      try {
        await abortOrderMutation({ variables: { id: order._id } });
      } catch (e) { /* left PAYMENT_PENDING — never charged, never sent to the restaurant */ }
      orderPlacedRef.current = false;
      setPlacedOrderId(null);
      setPlacingPhase(null);
      showMessage({ type: "error", message });
    };

    const stripeForm = stripeFormRef.current;
    if (!stripeForm) {
      await giveBack("The card form closed before payment. Please try again.");
      return;
    }

    let clientSecret = null;
    try {
      const res = await fetch(`${SERVER_URL}stripe/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: order._id }),
      });
      const body = await res.json();
      clientSecret = body.clientSecret || null;
      if (!clientSecret) {
        await giveBack(body.error || "Could not start the payment. Please try again.");
        return;
      }
    } catch (e) {
      await giveBack("Network error starting the payment. Please try again.");
      return;
    }

    // Flushed so the leave warning is gone before Stripe could navigate away.
    flushSync(() => setStripeConfirming(true));
    let result;
    try {
      result = await stripeForm.stripe.confirmPayment({
        elements: stripeForm.elements,
        clientSecret,
        redirect: "if_required",
        confirmParams: {
          // Only used by methods that must leave the page to authorise. The
          // Stripe screen picks the order up on the way back and confirms it.
          return_url: `${window.location.origin}/stripe?id=${order._id}`,
        },
      });
    } catch (e) {
      result = { error: e };
    }
    // Still here, so nothing navigated away: the warning is useful again.
    setStripeConfirming(false);
    const { error, paymentIntent } = result;

    let intent = paymentIntent;
    if (error) {
      // A decline leaves the intent unpaid, but a dropped connection can hide a
      // charge that went through. Ask Stripe before deleting the order.
      const check = await stripeForm.stripe.retrievePaymentIntent(clientSecret).catch(() => null);
      intent = check?.paymentIntent;
      if (!isPaid(intent)) {
        await giveBack(error.message || "Your payment was not completed. Please try again.");
        return;
      }
    } else if (!isPaid(intent)) {
      await giveBack("Your payment was not completed. Please try again.");
      return;
    }

    // Money has moved. Marking the order paid (and telling the restaurant) is
    // idempotent server side, so a flaky call is retried rather than surfaced.
    let confirmed = null;
    for (let attempt = 0; attempt < 3 && !confirmed; attempt += 1) {
      if (attempt) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, attempt * 1500));
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(`${SERVER_URL}stripe/confirm-payment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentIntentId: intent.id, orderId: order._id }),
        });
        // eslint-disable-next-line no-await-in-loop
        const body = await res.json();
        if (body.success) confirmed = body;
      } catch (e) { /* retried */ }
    }
    if (!confirmed) {
      // Never hand a charged customer back to a live Place Order button. The
      // payment_intent.succeeded webhook confirms the order on its own.
      console.error(`[Stripe] order ${order._id} charged but not confirmed from checkout`);
    }

    setPlacingPhase("success");
    const confirmedAt = Date.now();
    try {
      if (confirmed?.loyaltyEarnedMessage) {
        sessionStorage.setItem("loyaltyEarnedMessage", confirmed.loyaltyEarnedMessage);
      }
    } catch (e) { /* private-mode storage */ }
    // Same finish as QOR in onCompleted: refetch so order detail can find the
    // order, dwell so the confirmation is readable, navigate, THEN empty the cart.
    try {
      if (typeof fetchOrders === "function") await fetchOrders();
    } catch (e) { /* order detail refetches once on its own */ }
    const dwell = 1200 - (Date.now() - confirmedAt);
    if (dwell > 0) await new Promise((r) => setTimeout(r, dwell));
    navigate(`/order-detail/${order._id}`, { replace: true });
    try {
      await clearCart();
    } catch (e) { /* a stuck cart must not strand the customer on checkout */ }
  }

  function calculatePrice(delivery = 0, withDiscount) {
    let itemTotal = 0;
    cart.forEach((cartItem) => {
      itemTotal += cartItem.price * cartItem.quantity;
    });

    if (withDiscount) {
      // Prefer server-side promotion discount; fall back to legacy coupon % off
      if (promoResult && promoResult.discountAmount > 0) {
        itemTotal = Math.max(0, itemTotal - promoResult.discountAmount);
      } else if (coupon && coupon.discount) {
        itemTotal = itemTotal - (coupon.discount / 100) * itemTotal;
      }
      // Loyalty points-redemption coupon discount
      if (loyaltyCouponDiscount > 0) {
        itemTotal = Math.max(0, itemTotal - loyaltyCouponDiscount);
      }
    }

    // Use server-side discounted delivery fee only when delivery was requested
    const effectiveDelivery = delivery > 0
      ? (withDiscount && promoResult && promoResult.discountedDeliveryFee !== undefined
          ? promoResult.discountedDeliveryFee
          : deliveryCharges)
      : 0;

    return (itemTotal + effectiveDelivery).toFixed(2);
  }

  // The service fee card payments carry (Stripe and QOR). The server serves 0
  // until it starts charging it, so the line and the charge switch on together.
  // The restaurant's own fee is the one placeOrder charges, so prefer it and
  // fall back to the platform figure only while the field is missing.
  function calculateServiceFee() {
    // A catering cart pays the catering service fee the server quotes for it.
    const fee = parseFloat(
      isCateringCart && restaurantData?.catering?.serviceFee != null
        ? restaurantData.catering.serviceFee
        : restaurantData?.serviceFee != null ? restaurantData.serviceFee : configuration?.serviceFee || 0
    );
    const cardPayment = paymentMethod?.payment === "STRIPE" || paymentMethod?.payment === "QOR";
    return cardPayment && fee > 0 ? fee : 0;
  }

  function calculateTotal() {
    let total = 0;
    const delivery = isPickUp ? 0 : deliveryCharges;
    total += +calculatePrice(delivery, true);
    total += +taxCalculation();
    total += +calculateTip();
    total += calculateServiceFee();
    // Subtract the server-authoritative Black Card delivery discount. This is
    // already gated to eligible members within 5 miles (free up to $7) by the
    // backend, so it matches exactly what placeOrder charges.
    if (!isPickUp && blackCardDiscount > 0) {
      total = Math.max(0, total - blackCardDiscount);
    }
    return parseFloat(total).toFixed(2);
  }

  // Nested selections go as deep as the customer went, so this mirrors the tree
  // instead of flattening it. The GraphQL input types are mutually recursive, so
  // any depth ships as-is and the server re-prices every level.
  function mapChildSelections(groups) {
    return (groups || []).map((cg) => ({
      modifierGroupId: cg.modifierGroupId,
      modifierGroupName: cg.modifierGroupName,
      selectedModifiers: (cg.selectedModifiers || []).map((cm) => ({
        modifierId: String(cm.modifierId),
        modifierName: cm.modifierName,
        price: cm.price,
        quantity: cm.quantity || 1,
        ...(cm.childSelections?.length
          ? { childSelections: mapChildSelections(cm.childSelections) }
          : {}),
      })),
    }));
  }

  function transformOrder(cartData) {
    return cartData.map((food) => {
      // A food that contains an item-as-modifier add-on can't use the flat legacy
      // `addons: [ids]` format (it can't carry the add-on's nested selections), so
      // it ships via the richer `modifierSelections` instead. Foods without item
      // add-ons keep the exact legacy path (unchanged behaviour).
      // A plain modifier carrying follow-up groups needs the same rich format.
      const hasItemMember = (food.addons || []).some((a) =>
        (a.options || []).some((o) => o.isItemMember || o.nested)
      );

      if (hasItemMember) {
        return {
          food: food._id,
          quantity: food.quantity,
          variation: food.variation._id,
          // Menu the line was ordered from → server applies that menu's markup.
          menuId: food.menuId || null,
          addons: [],
          modifierSelections: (food.addons || []).map((a) => ({
            modifierGroupId: a._id,
            modifierGroupName: a.title || "",
            selectedModifiers: (a.options || []).map((o) => {
              const qty = o._qty && o._qty > 1 ? o._qty : 1;
              if (o.isItemMember) {
                return {
                  modifierId: String(o.itemId),
                  modifierName: o.title || "",
                  price: o.price || 0,
                  quantity: 1,
                  memberType: "ITEM",
                  itemId: String(o.itemId),
                  itemVariation: o.nested?.variant || null,
                  childSelections: mapChildSelections(o.nested?.childSelections),
                };
              }
              // A plain modifier can carry follow-up groups of its own.
              if (o.nested?.childSelections?.length) {
                return {
                  modifierId: String(o._id),
                  modifierName: o.title || "",
                  price: o.price || 0,
                  quantity: qty,
                  childSelections: mapChildSelections(o.nested.childSelections),
                };
              }
              return {
                modifierId: String(o._id),
                modifierName: o.title || "",
                price: o.price || 0,
                quantity: qty,
              };
            }),
          })),
          specialInstructions: food.specialInstructions,
          unitPrice: food.price ? parseFloat(food.price) : undefined,
        };
      }

      return {
        food: food._id,
        quantity: food.quantity,
        variation: food.variation._id,
        // Menu the line was ordered from → server applies that menu's markup.
        menuId: food.menuId || null,
        addons: food.addons
          ? food.addons.map(({ _id, options }) => ({
              _id,
              options: options.flatMap(({ _id, _qty }) => Array(_qty > 1 ? _qty : 1).fill(_id)),
            }))
          : [],
        specialInstructions: food.specialInstructions,
        unitPrice: food.price ? parseFloat(food.price) : undefined,
      };
    });
  }

  function checkPaymentMethod(currency) {
    if (paymentMethod.payment === "STRIPE") {
      return stripeCurrencies.find((val) => val.currency === currency);
    }
    if (paymentMethod.payment === "PAYPAL") {
      return paypalCurrencies.find((val) => val.currency === currency);
    }
    return true;
  }

  // Cart items whose menu(s) aren't open at the order's TARGET time (the
  // scheduled slot, or now for ASAP). An item is available if ANY menu it
  // belongs to is open then (a menu with no hours is always open).
  function getUnavailableItemsAtOrderTime() {
    // Catering items are not tied to a menu's hours — they are ordered ahead
    // for whenever the customer wants them.
    if (isCateringCart) return [];
    const menus = restaurantData?.advancedMenus || [];
    if (!cart?.length || !menus.length) return [];
    const target = (!isAsap && selectedDate) ? new Date(selectedDate) : new Date();
    const targetMinutes = target.getHours() * 60 + target.getMinutes();
    const isMenuOpenAt = (m) => {
      if (!m.startTime || !m.endTime) return true;
      const [sh, sm] = m.startTime.split(':').map(Number);
      const [eh, em] = m.endTime.split(':').map(Number);
      const start = sh * 60 + sm;
      const end = eh * 60 + em;
      if (start <= end) return targetMinutes >= start && targetMinutes <= end;
      return targetMinutes >= start || targetMinutes <= end; // overnight window
    };
    const unavailable = [];
    cart.forEach((ci) => {
      const itemId = String(ci._id);
      const itemMenus = menus.filter(
        (m) => m.isActive && (m.items || []).some((it) => String(it._id) === itemId)
      );
      if (itemMenus.length === 0) return; // not an advanced-menu item
      const availableInSome = itemMenus.some(isMenuOpenAt);
      if (!availableInSome) {
        unavailable.push({ key: ci.key, name: ci.title || ci.name || 'Item' });
      }
    });
    return unavailable;
  }

  // Opens the payment sheet from the payment block (pencil / "Add payment").
  // Picking a card here only changes what checkout will charge.
  function openQorSheet() {
    setQorSheetPurpose("manage");
    setQorModalOpen(true);
  }

  // Called by the sheet once a card is tokenized (or a wallet is authorized).
  // Opened to pay → charge it now, so the customer doesn't press Place Order
  // twice. Opened to manage → vault it, refresh the list, make it the default.
  async function onQorNewCard(token, wallet) {
    setQorToken(token);
    setQorModalOpen(false);
    if (qorSheetPurpose === "pay") {
      onPayment(token, wallet);
      return;
    }
    try {
      const res = await refetchQorCards();
      const cards = res?.data?.myQorSavedCards ?? [];
      const match = cards.find((c) => c.token === token);
      setQorSavedCardToken(match ? match.token : (cards[0]?.token ?? null));
    } catch (e) {
      // The list refuses to refresh — the card is still vaulted, and the next
      // load will show it. Don't strand the customer without a selection.
      setQorSavedCardToken(token);
    }
  }

  function onQorError(message) {
    showMessage({ type: "error", message: message || "Card could not be processed." });
  }

  async function onPayment(qorTokenArg, qorWalletArg, savedTokenArg) {
    // Saved token is passed explicitly (arg) to avoid stale React state — fall
    // back to the state value only when no arg was given.
    const effectiveSaved =
      savedTokenArg !== undefined ? savedTokenArg : qorSavedCardToken;
    // QorCommerce: if a SAVED card is selected, charge it directly — no iframe.
    // Otherwise, if we don't have a fresh token yet, open the capture form.
    // onQorNewCard() re-invokes onPayment with the token.
    if (paymentMethod?.payment === "QOR" && !qorTokenArg && !effectiveSaved) {
      if (qorCardsLoading) {
        showMessage({ type: "info", message: "Loading your payment methods…" });
        return;
      }
      setQorSheetPurpose("pay");
      setQorModalOpen(true);
      return;
    }
    const unavailable = getUnavailableItemsAtOrderTime();
    if (unavailable.length > 0) {
      const names = unavailable.map((u) => u.name).join(', ');
      const whenLabel = (!isAsap && selectedDate)
        ? new Date(selectedDate).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : 'the current time';
      // OK = remove the unavailable items and continue with the rest.
      // Cancel = re-open the scheduling modal so they can pick a different time.
      const removeThem = window.confirm(
        `These items aren't available at ${whenLabel}:\n\n${names}\n\n` +
        `• Click OK to remove these items and keep the rest of your order.\n` +
        `• Click Cancel to choose a different delivery/pickup time when these items are available.`
      );
      if (removeThem) {
        for (const u of unavailable) {
          // eslint-disable-next-line no-await-in-loop
          await deleteItem(u.key);
        }
        if (cart.length - unavailable.length <= 0) {
          showMessage({ type: "warning", message: "Your cart is now empty." });
        }
      } else {
        // They want to keep the items → send them back to scheduling.
        focusOrderOptions();
      }
      return;
    }
    if (!isPickUp && !deliveryAvailable) {
      showMessage({
        type: "error",
        message: noZonesConfigured
          ? "This restaurant is not delivering right now. Please try pickup or check back later."
          : "Delivery is not available to your address. Please change your delivery address.",
      });
      return;
    }
    if (checkPaymentMethod(configuration.currency)) {
      if (paymentMethod?.payment === "STRIPE") {
        const stripeForm = stripeFormRef.current;
        if (!stripeForm) {
          showMessage({ type: "info", message: "Loading the card form…" });
          return;
        }
        // Validates the card fields before any order exists, so a typo costs
        // nothing. Stripe marks the bad field inline; the toast is for when the
        // form has scrolled out of view behind the sticky Place Order rail.
        const { error: submitError } = await stripeForm.elements.submit();
        if (submitError) {
          showMessage({ type: "error", message: submitError.message || "Check your card details." });
          return;
        }
        // Up from here until order detail — see payStripeOrder.
        setPlacingPhase("processing");
      }
      const items = transformOrder(cart);
      mutateOrder({
        variables: {
          restaurant: cartRestaurant,
          orderInput: items,
          paymentMethod: paymentMethod.payment,
          couponCode: loyaltyVoucherCode || (promoResult?.appliedPromotion ? (promoResult.appliedPromotion._id ? coupon?.title : null) : coupon?.title) || null,
          loyaltyCouponCode: loyaltyCouponCode || null,
          selectedPromoId: selectedPromoId || null,
          tipping: +calculateTip(),
          serverTip: calculateServerTip(),
          driverTip: calculateDriverTip(),
          taxationAmount: +taxCalculation(),
          address: {
            label: location.label,
            deliveryAddress: location.deliveryAddress,
            details: location.details,
            apartment: location.apartment || null,
            buildingName: location.buildingName || null,
            entryCode: location.entryCode || null,
            dropoffOption: dropoffOption || location.dropoffOption || null,
            instructions: location.instructions || null,
            longitude: parseFloat(location.longitude),
            latitude: parseFloat(location.latitude),
          },
          orderDate: selectedDate,
          isPickedUp: isPickUp,
          deliveryCharges: isPickUp ? 0 : deliveryCharges,
          // Fall back to the saved address's instructions when the customer
          // didn't type any at checkout.
          instructions: deliveryInstructions || location.instructions || null,
          walletAmountToUse: walletAmountToUse > 0 ? walletAmountToUse : undefined,
          qorPaymentToken:
            paymentMethod?.payment === "QOR" && qorTokenArg && qorTokenArg !== "__saved__"
              ? qorTokenArg
              : (paymentMethod?.payment === "QOR" ? null : undefined),
          qorWallet:
            paymentMethod?.payment === "QOR" && qorWalletArg
              ? JSON.stringify(qorWalletArg)
              : undefined,
          qorSavedCardToken:
            paymentMethod?.payment === "QOR" && effectiveSaved ? effectiveSaved : undefined,
          qorSaveCard:
            paymentMethod?.payment === "QOR" && !effectiveSaved && qorSaveCard ? true : undefined,
          // Required for a catering cart; the server refuses one sent without it.
          isCatering: isCateringCart,
          // The food-truck stop this cart is for.
          stopId: cartStopId,
        },
      });
    } else {
      showMessage({
        type: "warning",
        message: "Payment not supported",
      });
    }
  }

  const locationCallback = (error, data) => {
    setLoadingLocation(false); // Stop loading
    if (error) {
      console.error(error);
      return;
    }

    // Create an object for the selected address using the current location data
    const selectedAddress = {
      label: "Your Location",
      deliveryAddress: data.label,
      details: data.details,
      latitude: data.coords.latitude,
      longitude: data.coords.longitude,
    };

    // Update the selected address in state
    setSelectedAddress(selectedAddress);
    setAddressModal((prev) => !prev);
  };

  const getCurrentLocation = () => {
    setLoadingLocation(true); // Start loading (optional)

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          locationCallback(null, position);
        },
        (error) => {
          locationCallback(error, null);
        }
      );
    } else {
      // Geolocation is not supported by the browser
      locationCallback(new Error("Geolocation is not supported."), null);
    }
  };

  // A scheduled (non-ASAP) order with a future date is allowed even when the
  // restaurant is currently closed — it'll be prepared at the chosen time.
  const hasFutureScheduledOrder = () =>
    !isAsap && selectedDate && new Date(selectedDate).getTime() > Date.now() + 60000;

  function validateOrder() {
    if ((!data.restaurant.isAvailable || !isOpen()) && !hasFutureScheduledOrder()) {
      toggleCloseModal();
      return;
    }
   
    if (!cart.length) {
      showMessage({
        type: "error",
        message: "Cart is Empty.",
      });
      return false;
    }
    // Schedule for later with no time chosen would otherwise go out as ASAP.
    if (!isCateringCart && !isAsap && !hasFutureScheduledOrder()) {
      showMessage({ type: "warning", message: "Choose a time for your scheduled order." });
      focusOrderOptions();
      return false;
    }
    // A catering order has to be wanted at least the notice ahead. The time
    // picker already keeps to that; this catches a choice that went stale.
    if (isCateringCart) {
      const lead = cateringSettings?.leadTimeMinutes || 0;
      const wanted = selectedDate ? new Date(selectedDate).getTime() : NaN;
      if (lead > 0 && (isAsap || !Number.isFinite(wanted) || wanted < Date.now() + (lead - 10) * 60000)) {
        showMessage({
          type: "warning",
          message: `Catering from ${restaurantData?.name || "this restaurant"} needs ${describeLead(lead)} of notice. Please pick a later time.`,
        });
        focusOrderOptions();
        return false;
      }
    }
    // The minimum is met by the items alone, after discounts. Delivery, tax,
    // tips and the service fee don't count; placeOrder enforces the same rule.
    // A catering cart has its own minimum (none when it isn't set).
    const minimumOrder = isCateringCart
      ? parseFloat(cateringSettings?.minimumOrder) || 0
      : parseFloat(restaurantData?.minimumOrder) || 0;
    if (parseFloat(calculatePrice(0, true)) < minimumOrder) {
      showMessage({
        type: "warning",
        message: `The minimum amount of (${configuration.currencySymbol} ${minimumOrder}) for your order has not been reached.`,
      });
      return false;
    }
    if (!location) {
      showMessage({
        alive: true,
        type: "Warning",
        message: "Select your address.",
      });
      return false;
    }
    // For delivery, require a full street address (city-only addresses like
    // "Salem, MA" aren't deliverable). A street address has a house/building
    // number in its first segment — block and open Change Address if missing.
    if (!isPickUp) {
      const firstSegment = (location.deliveryAddress || location.label || "").split(",")[0];
      const hasStreetNumber = /\d/.test(firstSegment);
      if (!hasStreetNumber) {
        showMessage({
          alive: true,
          type: "error",
          message:
            "Please change your address — enter your full street address (with house/building number) to place a delivery order.",
        });
        toggleAdressModal();
        return false;
      }
      // Delivery requires a SAVED address (so we capture apt/house + entry code
      // + instructions). If the current location was never saved (no _id), open
      // the structured detail form prefilled with it and block the order — same
      // as the app.
      if (!location._id) {
        showMessage({
          alive: true,
          type: "warning",
          message: t("saveDeliveryAddressFirst"),
        });
        setForceAddressDetail(true);
        setAddressModal(true);
        return false;
      }
    }
    if (!paymentMethod) {
      showMessage({
        type: "warning",
        message: "Set payment method before checkout",
      });
      return false;
    }
    // Phone verification not required
    return true;
  }
  const placeOrderNow = () => {
    if (validateOrder()) onPayment();
  };
  const totalLabel = money(calculateTotal(), currency);
  // RMF Wallet: the amount typed (or Use All), never more than the balance or
  // the order total.
  const onWalletInput = (val) => {
    setWalletInputValue(val);
    const n = parseFloat(val) || 0;
    setWalletAmountToUse(Math.min(n, walletAvailable, parseFloat(calculateTotal())));
  };
  const onWalletUseAll = () => {
    const max = Math.min(walletAvailable, parseFloat(calculateTotal()));
    setWalletInputValue(max.toFixed(2));
    setWalletAmountToUse(max);
  };
  const noteLinkSx = { whiteSpace: "nowrap", color: C.redLink, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px", borderRadius: "4px", ...FOCUS_RING };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg, pb: { xs: "112px", md: 3 } }}>
      <FlashMessage
        open={Boolean(mainError.type)}
        severity={mainError.type}
        alertMessage={mainError.message}
        handleClose={toggleSnackbar}
        alive={mainError.alive || false}
      />

      <CheckoutHeader backTo={backTo} />

      <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 4 } }}>
        <Box component="h1" sx={{ ...TYPE.pageTitle, m: 0, mb: { xs: 2.5, md: 3 }, color: C.ink, fontSize: { xs: 28, sm: 34 } }}>
          Checkout
        </Box>
        {isCateringCart && (
          <CateringOrderBanner
            settings={cateringSettings}
            currencySymbol={currency}
          />
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) 360px",
              lg: "minmax(0, 1fr) 400px",
              xl: "minmax(0, 1fr) 420px",
            },
            gap: { xs: 3, md: 4 },
            alignItems: "start",
          }}
        >
          {/* The bottom margin sits inside the grid row, so at the end of the
              page the summary still has room below the header. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, mb: { md: 5 } }}>
            {/* 1 — Where and when. Two flows land here instead of opening a
                dialog — the closed-restaurant popup and the "item unavailable
                at that time" confirm — so it scrolls into view and flashes. */}
            <Box ref={orderOptionsRef}>
              <StepCard id="checkout-step-details" number={1} title={isPickUp ? "Pickup details" : "Delivery details"}>
                <OrderOptions
                  isPickUp={isPickUp}
                  setIsPickUp={choosePickUp}
                  isAsap={isAsap}
                  setIsAsap={setIsAsap}
                  selectedDate={selectedDate}
                  handleDateChange={isCateringCart ? chooseCateringDate : chooseOrderDate}
                  prepTime={restaurantData?.preparationTime ?? 15}
                  openingTimes={restaurantData?.openingTimes || []}
                  isAvailable={restaurantData?.isAvailable ?? true}
                  restaurantData={restaurantData}
                  highlight={highlightOrderOptions}
                  isSelfDelivery={isSelfDelivery}
                  deliveryFeeLoaded={deliveryFeeLoaded}
                  catering={cateringSettings}
                  addressCard={
                    <CheckoutAddressCard
                      isPickUp={isPickUp}
                      location={location}
                      restaurant={restaurantData}
                      restaurantCoords={restCoordinates}
                      onChangeAddress={toggleAdressModal}
                    />
                  }
                >
                  <DropoffAndNotes
                    isPickUp={isPickUp}
                    dropoffOption={dropoffOption}
                    setDropoffOption={setDropoffOption}
                    instructions={deliveryInstructions}
                    setInstructions={onChangeDeliveryInstructions}
                  />
                </OrderOptions>
              </StepCard>
            </Box>

            {/* 2 — Who it's for */}
            <StepCard
              id="checkout-step-contact"
              number={2}
              title="Contact"
              action={
                <Box component={RouterLink} to="/profile" aria-label="Edit contact details" sx={textButtonSx}>
                  Edit
                </Box>
              }
            >
              <ContactDetails profile={profile} />
            </StepCard>

            {/* 3 — Wallet first, then what pays the rest */}
            <StepCard id="checkout-step-payment" number={3} title="Payment">
              {walletAvailable > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <WalletCard
                    available={walletAvailable}
                    storeCredit={storeCreditHere}
                    inputValue={walletInputValue}
                    onInputChange={onWalletInput}
                    onUseAll={onWalletUseAll}
                    applied={walletAmountToUse}
                    chargedToCard={Math.max(0, parseFloat(calculateTotal()) - walletAmountToUse)}
                    currencySymbol={currency}
                  />
                </Box>
              )}
              <PaymentCard
                heading="Pay with"
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                isPickUp={isPickUp}
                enablePickupCOD={!!restaurantData?.enablePickupCOD}
                savedCards={qorCards}
                selectedCardToken={qorSavedCardToken}
                cardsLoading={qorCardsLoading}
                onOpenPaymentSheet={openQorSheet}
                stripeHandleRef={stripeFormRef}
                stripeAmount={Math.max(0, parseFloat(calculateTotal()) - walletAmountToUse)}
              />
            </StepCard>

            {/* Carts parked for other restaurants */}
            <OtherCarts />
          </Box>

          {/* The order and its money, in view the whole way down the steps. */}
          <Box
            component="aside"
            ref={summaryRef}
            aria-label="Order summary"
            sx={{ position: { md: "sticky" }, top: { md: summaryTop }, minWidth: 0 }}
          >
            <CartItemCard
              summaryData={summaryData}
              promoPending={promoPending}
              setSelectedTip={setSelectedTip}
              selectedTip={selectedTip}
              tipDecided={tipDecided}
              customTipAmount={customTipAmount}
              setCustomTipAmount={setCustomTipAmount}
              setTaxValue={setTaxValue}
              setCoupon={setCoupon}
              promoResult={promoResult}
              selectedPromoId={selectedPromoId}
              setSelectedPromoId={(id) => {
                setSelectedPromoId(id);
                if (id) setCoupon({});
              }}
              setLoyaltyVoucherCode={setLoyaltyVoucherCode}
              loyaltyCouponCode={loyaltyCouponCode}
              setLoyaltyCouponCode={(code, discount) => {
                setLoyaltyCouponCode(code);
                setLoyaltyCouponDiscount(code ? (parseFloat(discount) || 0) : 0);
              }}
              cartRestaurant={cartRestaurant}
              restaurantData={restaurantData}
              setFlashMessage={showMessage}
              calculateTotal={calculateTotal}
              calculatePrice={calculatePrice}
              taxCalculation={taxCalculation}
              calculateTip={calculateTip}
              calculateServiceFee={calculateServiceFee}
              isPickUp={isPickUp}
              isSelfDelivery={isSelfDelivery}
              deliveryCharges={deliveryCharges}
              memberDiscount={!isPickUp ? blackCardDiscount : 0}
              addQuantity={addQuantity}
              removeQuantity={removeQuantity}
              onClearCart={() => {
                setCartCleared(true);
                clearCart();
                navigate(backTo, { replace: true });
              }}
              footer={
                <>
                  <PlaceOrderButton amount={totalLabel} onClick={placeOrderNow} loading={loadingOrderMutation} />
                  <Box component="p" sx={{ ...TYPE.meta, m: 0, mt: 1.25, color: C.muted, textAlign: "center" }}>
                    By placing your order you agree to our{" "}
                    <Box component={RouterLink} to="/terms" sx={noteLinkSx}>Terms</Box>
                    {" "}and{" "}
                    <Box component={RouterLink} to="/privacy" sx={noteLinkSx}>Privacy Policy</Box>
                  </Box>
                </>
              }
            />
          </Box>
        </Box>
      </Box>

      {/* Narrow windows: the summary sits below the steps, so Place order
          also rides along the bottom of the screen. */}
      {!twoColumn && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            bgcolor: C.bg,
            borderTop: `1px solid ${C.border}`,
            px: 2,
            pt: 1.5,
            pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <PlaceOrderButton amount={totalLabel} onClick={placeOrderNow} loading={loadingOrderMutation} />
        </Box>
      )}

      {!isPickUp && (
        <Dialog
          fullScreen={extraSmall}
          onClose={toggleAdressModal}
          open={addressModal}
          maxWidth="md"
          aria-labelledby="checkout-address-dialog-title"
          PaperProps={{
            style: {
              borderRadius: extraSmall ? 0 : 20,
              overflowY: "scroll",
              height: extraSmall ? 500 : null,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 3, pt: 2.5 }}>
            <Box component="h2" id="checkout-address-dialog-title" sx={{ ...TYPE.cardTitle, m: 0, color: C.ink }}>
              {t("deliverTo")}
            </Box>
            <IconButton onClick={toggleAdressModal} aria-label="Close" sx={{ width: 44, height: 44, color: C.ink }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {location && (
            <Box sx={{ px: 3, mt: 0.5 }}>
              {location.label && <Box sx={{ ...TYPE.subHeading, color: C.ink }}>{location.label}</Box>}
              <Box sx={{ ...TYPE.meta, color: C.muted }}>{location.deliveryAddress}</Box>
            </Box>
          )}
          <Box sx={{ px: 3, py: 2 }}>
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                getCurrentLocation();
              }}
              sx={{
                ...BUTTON_RESET,
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                minHeight: 56,
                boxSizing: "border-box",
                borderRadius: RADIUS.controlLg,
                border: `1px solid ${C.borderStrong}`,
                "&:hover": { borderColor: C.red, bgcolor: C.redTint },
                ...FOCUS_RING,
              }}
            >
              <Box aria-hidden="true" sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "50%", bgcolor: C.redTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <NearMeIcon sx={{ fontSize: 20, color: C.red }} />
              </Box>
              <Box component="span" sx={{ ...TYPE.subHeading, flex: 1, color: C.ink, fontFamily: FONT_BODY }}>
                {t("currentLocation")}
              </Box>
              {loadingLocation && <CircularProgress size={20} sx={{ color: C.red }} />}
            </Box>
          </Box>
          <Box sx={{ borderTop: `1px solid ${C.border}` }} />
          <DeliveryCard
            selectedAddress={selectedAddress}
            setSelectedAddress={setDeliveryAddress}
            isProfile={true}
            isCheckout={true}
            forceDetail={forceAddressDetail}
            onAddressSaved={(saved) => {
              // Saved from the forced detail form: make it the active
              // delivery address and close the modal so the customer
              // can place the order.
              setDeliveryAddress(saved);
              setForceAddressDetail(false);
              setAddressModal(false);
            }}
            close={toggleAdressModal}
          />
        </Dialog>
      )}

      <RestaurantClose
        isVisible={isClose}
        toggleModal={toggleCloseModal}
        restaurant={restaurantData}
        onScheduled={focusOrderOptions}
        onBrowse={() => navigate(backTo)}
      />
      {/* QorCommerce payment sheet — saved cards, then add a new one. The
          charge itself always happens on Place Order. */}
      <QorPaymentSheet
        open={qorModalOpen}
        onClose={() => setQorModalOpen(false)}
        cards={qorCards}
        loading={qorCardsLoading}
        selectedToken={qorSavedCardToken}
        onSelect={(token) => setQorSavedCardToken(token)}
        onCardsChanged={() => refetchQorCards()}
        onNewCard={onQorNewCard}
        profileId={profile?._id ? String(profile._id) : undefined}
        amount={calculateTotal()}
        onError={onQorError}
        purpose={qorSheetPurpose}
      />
      {/* placeOrder charges the card inside the request — measured at ~15s, of
          which ~8s is the payment gateway alone. A spinner inside the button is
          not enough feedback for that long: this blocks the page, keeps the
          customer informed, and tells them not to close the window. */}
      <PaymentProcessing
        // placingPhase outlives the mutation's own loading flag on purpose: a
        // `success` must stay up until order detail takes over, and a Stripe
        // `processing` must span placeOrder AND the charge that follows it.
        // paymentMethod is an OBJECT here; the overlay wants the string, and
        // passing the object silently fell through to the cash copy on card
        // orders.
        phase={placingPhase || (loadingOrderMutation ? "processing" : null)}
        amount={calculateTotal()}
        currencySymbol={currency}
        paymentMethod={paymentMethod?.payment}
        orderId={placedOrderId}
        warnOnLeave={!stripeConfirming}
      />
    </Box>
  );
}

export default Checkout;
